import { type ReactNode, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Paper from '@mui/material/Paper';
import type { DateRange, Nullish } from '@witmetrics/utils';
import { isNullish } from '@witmetrics/utils/misc';
import {
  getEndOfDay,
  getStartOfDay,
  isBeforeDate,
} from '@witmetrics/utils/time';
import { useToggle } from '@/hooks/useToggle';
import DateRangeCalendar from '@/components/DateRangeCalendar';
import Button from '@/components/Button';
import DateRangeFields from './DateRangeFields';
import DateRangeTextInput from './DateRangeTextInput';
import DefaultOptionsList from './DefaultOptionsList';
import { ACTION } from './constants';
import {
  classes,
  getInitialEnd,
  getInitialStart,
  isCalendarDay,
  styles,
} from './utils';

const { PICK_START, PICK_END } = ACTION;

export type DateRangeInputProps<N extends string> = {
  disablePast?: boolean;
  sx?: FormControlProps['sx'];
  className?: string;
  name: N;
  icon?: ReactNode;
  placeholder?: string;
  value: DateRange | Nullish;
  onChange?: (update: Record<N, DateRange>) => void;
  onApply?: (update: Record<N, DateRange>) => void;
  onReset?: () => void;
};

export default function DateRangeInput<N extends string>({
  disablePast = false,
  sx,
  className,
  name,
  icon,
  placeholder,
  value,
  onChange,
  onApply,
  onReset,
}: DateRangeInputProps<N>) {
  const inputRef = useRef(null);
  const [currentAction, setCurrentAction] = useState(PICK_START);
  const [start, setStart] = useState(getInitialStart(value));
  const [end, setEnd] = useState(getInitialEnd(value));
  const [activeDate, setActiveDate] = useState(start);
  const [isOpen, toggleOpen] = useToggle(false);

  useEffect(() => {
    if (onChange) onChange({ [name]: [start, end] } as Record<N, DateRange>);
  }, [start, end]);

  useEffect(() => {
    // Enter on a focused day applies; Escape is handled by the popover's onClose
    const checkKeyPress = (e: KeyboardEvent) => {
      handleKeyPress(e);
    };
    if (isOpen) {
      window.addEventListener('keyup', checkKeyPress);
    } else {
      window.removeEventListener('keyup', checkKeyPress);
    }
    return () => {
      window.removeEventListener('keyup', checkKeyPress);
    };
  }, [isOpen, start, end]);

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' || !isCalendarDay(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    handleApply();
  };

  const handleClose = () => {
    if (onApply) {
      setStart(getInitialStart(value));
      setEnd(getInitialEnd(value));
    }
    toggleOpen(false);
  };

  const handleApply = () => {
    if (onApply) {
      onApply({ [name]: [start, end] } as Record<N, DateRange>);
    }
    toggleOpen(false);
  };

  const handleReset = () => {
    onReset?.();
    toggleOpen(false);
  };

  const handleSelectDate = (date: Date) => {
    const startDate = getStartOfDay(date.toISOString());
    const endDate = getEndOfDay(date.toISOString());
    if (currentAction === PICK_START) {
      setStart(startDate);
      setEnd(endDate);
      setActiveDate(startDate);
      setCurrentAction(PICK_END);
    } else {
      if (isBeforeDate(date.toISOString(), start)) {
        // Values are reversed
        setStart(startDate);
        setEnd(getEndOfDay(start));
        setActiveDate(startDate);
        setCurrentAction(PICK_START);
      } else {
        setEnd(endDate);
        setActiveDate(endDate);
        setCurrentAction(PICK_START);
      }
    }
  };

  const handleChangeStart = (date: string) => {
    const startDate = getStartOfDay(date);
    setStart(startDate);
    if (isBeforeDate(end, startDate)) setEnd(getEndOfDay(date));
    setActiveDate(startDate);
    setCurrentAction(PICK_START);
  };

  const handleChangeEnd = (date: string) => {
    const endDate = getEndOfDay(date);
    setEnd(endDate);
    if (isBeforeDate(endDate, start)) setStart(getStartOfDay(date));
    setActiveDate(endDate);
    setCurrentAction(PICK_START);
  };

  const handleSelectDefaultTimeframe = (timeframe: DateRange) => {
    setStart(timeframe[0]);
    setEnd(timeframe[1]);
    setActiveDate(timeframe[1]);
    setCurrentAction(PICK_START);
  };

  const renderButtons = () => {
    if (!onApply && !onReset) return null;
    return (
      <div className={classes.buttonWrapper}>
        {onReset && (
          <Button
            className={classes.resetButton}
            variant="outlined"
            borderClasses={classes.resetBorder}
            textClasses={classes.resetText}
            onClick={handleReset}>
            Reset
          </Button>
        )}
        {onApply && (
          <Button
            className={classes.button}
            backgroundClasses={classes.buttonBackground}
            onClick={handleApply}>
            Apply
          </Button>
        )}
      </div>
    );
  };

  // With no value the field shows its placeholder until the user starts picking
  const showRange = !isNullish(value) || isOpen;

  return (
    <FormControl
      sx={sx}
      className={twMerge(classes.formControl, className)}
      size="small">
      <DateRangeTextInput
        ref={inputRef}
        open={isOpen}
        name={name}
        icon={icon}
        placeholder={placeholder}
        start={showRange ? start : null}
        end={showRange ? end : null}
        onClick={() => toggleOpen()}
      />
      <Popover
        open={isOpen}
        sx={styles.popover}
        anchorEl={inputRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={handleClose}>
        <Paper className={classes.paper}>
          <DefaultOptionsList onSelect={handleSelectDefaultTimeframe} />
          <Divider flexItem orientation="vertical" />
          <div className={classes.panel}>
            <DateRangeFields
              disablePast={disablePast}
              start={start}
              end={end}
              onChangeStart={handleChangeStart}
              onChangeEnd={handleChangeEnd}
            />
            <DateRangeCalendar
              disablePast={disablePast}
              activeDate={activeDate}
              start={start}
              end={end}
              onChange={handleSelectDate}
            />
            {renderButtons()}
          </div>
        </Paper>
      </Popover>
    </FormControl>
  );
}
